import { useEffect, useRef } from 'react'
import { useStore } from '@tanstack/react-store'
import { toast } from 'sonner'
import { useExtensionDetection } from '@/hooks/use-extension'
import { appStore, dismissExtensionPrompt } from '@/lib/store'
import { Button } from '@/components/ui/button'

/**
 * Extension install prompt component
 * Shows a toast prompting users to install the browser extension if not detected
 */
export function ExtensionInstallPrompt() {
  const { isInstalled, isChecking, storeUrl } = useExtensionDetection()
  const extensionPromptDismissed = useStore(appStore, (state) => state.extensionPromptDismissed)
  const hasShownToast = useRef(false)

  useEffect(() => {
    // Don't show if still checking, already installed, user dismissed, or already shown
    if (isChecking || isInstalled || extensionPromptDismissed || hasShownToast.current) {
      return
    }

    // Mark as shown to prevent duplicate toasts
    hasShownToast.current = true

    // Show toast after a brief delay to avoid appearing too aggressive
    const timer = setTimeout(() => {
      const toastId = toast(
        <div className="flex flex-col w-full gap-3">
          <div>
            <div className="text-base font-semibold">Fiddl Chrome Extension</div>
            <div className="mt-1 text-sm text-muted-foreground">
              Install the Chrome extension to get Fiddl Toolbar on your production site.
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                toast.dismiss(toastId)
                dismissExtensionPrompt()
              }}
              variant="outline"
              className="flex-1"
            >
              Dismiss
            </Button>
            <Button
              size="sm"
              onClick={() => {
                window.open(storeUrl, '_blank')
                toast.dismiss(toastId)
              }}
              className="flex-1"
            >
              Install
            </Button>
          </div>
        </div>,
        {
          duration: Infinity, // Don't auto-dismiss
        }
      )
    }, 2000)

    return () => clearTimeout(timer)
  }, [isChecking, isInstalled, extensionPromptDismissed, storeUrl])

  return null
}
